import SbButton from '@Components/Button';
import { AppDispatch, RootStateType } from '@Store';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BoltIcon from '@mui/icons-material/Bolt';
import { Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Image from '../../../../components/Image';
import SbTooltip from '../../../../components/Tooltip';
import { CSPChargePropsType, CSPChargeType } from '../../../../store/reducers/GetRates/Type';
import { BackToLoginModal } from '../BackToLoginModal';
import ChargesBreakup from '../ChargesBreakup';
import { BookBtn, CSPLogo, SbChip, TableData, TableRow } from '../styles';
import { setBookNowAuth } from '@Reducers/GetRates';
import { useDispatch } from 'react-redux';
import { setFinalChargeableWeight } from '@Reducers/Shipment';
import useAccountLookup from '@Hook/useAccountLookup';
import { Tooltip } from '@mui/material';
import { createShipmentContextType } from '../../../../types/shipment';
import CreateShipmentContext from '../../../../context/CreateShipment';

type Warehouse = {
  _id: string;
  _systemId: string[];
};

function getWarehouseIdBySystemId(
  systemId: string | undefined,
  warehouseListForShipment?: { list?: Warehouse[] },
): string | undefined {
  if (!systemId) return undefined; // Guard clause

  return warehouseListForShipment?.list?.find(warehouse => warehouse._systemId.includes(systemId))?._id;
}

function SingleCSP(props: CSPChargePropsType) {
  const { CSPCharge, handleBookCSP, renderedFrom, isSelected = false } = props;
  const [openBackToLoginModal, setBackToLoginModal] = useState(false);
  const {
    transitTime,
    serviceName,
    serviceImage,
    totalPricing,
    fastest,
    cheapest,
    pricing,
    taxPricing,
    displayChargeableWeight,
  } = CSPCharge;
  const modalId = 'GetRatesV2Main';
  const { selectedUser } = useAccountLookup(modalId);
  const shipmentContext = React.useContext(CreateShipmentContext) as createShipmentContextType;

  const pickupType = shipmentContext?.pickupType;
  const setPickupType = shipmentContext?.setPickupType;
  const packageData = shipmentContext?.packageData;
  const setPackageData = shipmentContext?.setPackageData;

  const [currency, setCurrency] = useState<{ name?: string; symbol?: string; fullForm?: string }>({});
  const { token } = useSelector((state: RootStateType) => state.loginUser);
  const dispatch = useDispatch<AppDispatch>();
  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const { warehouseListForShipmentOptionsHashById } = useSelector((state: RootStateType) => state.Warehouse);
  const countryHashById = useSelector((state: RootStateType) => state.global.countriesObj);

  useEffect(() => {
    const stored = localStorage.getItem('currency');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCurrency(parsed);
      } catch (e) {
        console.error('Failed to parse stored currency:', e);
      }
    }
  }, []);

  const bookCSP = (CSPCharge: CSPChargePropsType['CSPCharge'] | null) => {
    const warehouseId = getWarehouseIdBySystemId(CSPCharge?._systemId, warehouseListForShipent);
    if (
      warehouseId &&
      name === 'Cargo Solutions Network' &&
      setPackageData &&
      setPickupType &&
      packageData &&
      pickupType
    ) {
      const selectedWarehouse = warehouseListForShipmentOptionsHashById[warehouseId];
      const coAttention = selectedWarehouse.coAttention;
      const [telephoneCode, number] = coAttention.contact;
      const countryId = selectedWarehouse._addressId.country;
      const warehouseAddress = {
        ...selectedWarehouse._addressId,
        country: countryHashById?.[countryId]?.name,
        name: selectedWarehouse.name,
        _countryId: countryHashById?.[countryId]?._id,
        phone: { telephoneCode, number, name: coAttention.name },
        email: coAttention.email,
      };
      setPackageData({
        ...packageData,
        isDropOff: true,
        ...(CSPCharge?.currency && {
          currency: CSPCharge.currency || defaultOrgCountryCurrency,
        }),
      });
      setPickupType({ ...pickupType, selectedWearhouseAddress: warehouseAddress, warehouse: warehouseId });
    }
    if (token?._id) {
      handleBookCSP(CSPCharge);
      dispatch(setBookNowAuth(true));
    } else {
      setBackToLoginModal(true);
      dispatch(setBookNowAuth(false));
    }
  };

  const handleCloseBackToLoginModal = () => {
    setBackToLoginModal(false);
  };

  const isAccountSuspended = selectedUser?.onBoardedStatus ? selectedUser?.onBoardedStatus === 'suspended' : false;
  const { warehouseListForShipent } = useSelector((state: RootStateType) => state.Warehouse);

  return (
    <>
      <TableRow
        container
        item
        alignItems={'center'}
        xs={12}
        spacing={1}
        compactview={renderedFrom === 'admin-shipment-history' ? '1' : '0'}
      >
        <TableData item className="mr-2" xs={1} sm={1}>
          <CSPLogo>
            <img src={serviceImage} height={'100%'} width={'100%'} style={{ borderRadius: '10px' }} />
          </CSPLogo>
        </TableData>
        <TableData item sm={6} md={4} lg={5}>
          <Grid container wrap="nowrap" justifyItems={'start'}>
            <Grid container item className="w-auto" sm={8} md={7} lg={7.5}>
              <Grid item lg={12}>
                <Typography className="md-text font-semibold">
                  {name === 'Cargo Solutions Network' ? serviceName : transitTime + ' Days'}
                </Typography>
              </Grid>
              <Grid item lg={12}>
                <Typography className="sm-text">
                  {name === 'Cargo Solutions Network' ? transitTime + ' Days' : serviceName}
                </Typography>
              </Grid>
            </Grid>
            <Grid container item alignItems={'center'} sm={6} md={4.5} lg={4}>
              {fastest && (
                <SbChip
                  className="md-text font-semibold"
                  sx={{
                    color: '#127E44',
                    background: '#ADFFB5',
                    '& .MuiChip-icon': {
                      color: '#127E44',
                    },
                  }}
                  icon={<BoltIcon />}
                  label="Fastest"
                />
              )}
              {cheapest && (
                <SbChip
                  className="md-text font-semibold"
                  sx={{
                    color: '#12647E',
                    background: '#ADE6FF',
                    '& .MuiChip-icon': {
                      color: '#12647E',
                    },
                  }}
                  icon={<AccountBalanceWalletIcon />}
                  label="Cheapest"
                />
              )}
            </Grid>
          </Grid>
        </TableData>
        {/* <TableData item lg={3}></TableData> */}
        <TableData item sm={2} md={2} lg={1.5}>
          <Typography className="font-semibold text-center">{`${displayChargeableWeight?.toFixed(2)} kg`}</Typography>
        </TableData>
        <TableData item sm={2.5} md={2.5} lg={2}>
          <Grid container justifyContent={'center'}>
            <Typography className="md-text font-semibold mr-1 w-full" textAlign="center">
              {totalPricing
                ? `${CSPCharge?.currency || defaultOrgCountryCurrency || currency?.name || ''} ${parseFloat(
                    totalPricing?.toString(),
                  ).toFixed(2)}`
                : ''}
            </Typography>
            <SbTooltip
              title={
                <ChargesBreakup
                  pricing={pricing}
                  totalPricing={totalPricing}
                  taxPricing={taxPricing}
                  currency={CSPCharge?.currency || defaultOrgCountryCurrency || currency?.name || ''}
                />
              }
              placement={'left'}
            >
              <Grid
                container
                item
                alignItems={'center'}
                textAlign={'center'}
                justifyContent={'end'}
                style={{ cursor: 'default', width: 'fit-content' }}
              >
                <Image src={'/images/Boxes/Help.svg'} alt="help" style={{ height: '16px', width: '16px' }} />
                <Typography style={{ textDecoration: 'underline' }} className="ml-1/2">
                  View Breakup
                </Typography>
              </Grid>
            </SbTooltip>
          </Grid>
        </TableData>
        <TableData container item justifyContent={'end'} sm={12} md={2.2} lg={2}>
          {isSelected ? (
            <SbButton variant="contained" onClick={() => bookCSP(null)}>
              Selected
            </SbButton>
          ) : (
            <Tooltip
              title={isAccountSuspended ? 'This account is suspended' : ''}
              disableHoverListener={!isAccountSuspended}
            >
              <span>
                <BookBtn
                  variant="outlined"
                  className="font-semibold"
                  disabled={isAccountSuspended}
                  onClick={() => {
                    const res = displayChargeableWeight?.toFixed(2);
                    dispatch(setFinalChargeableWeight(parseFloat(res)));
                    bookCSP(CSPCharge);
                  }}
                  style={{ zIndex: '2' }}
                >
                  Book Now
                </BookBtn>
              </span>
            </Tooltip>
          )}
        </TableData>
      </TableRow>
      <BackToLoginModal open={openBackToLoginModal} onClose={handleCloseBackToLoginModal} />
    </>
  );
}

export function sortCSPs(charges: CSPChargeType[]): CSPChargeType[] {
  if (!charges || charges.length <= 1) return charges || [];

  const getTransitTimeValue = (transitTime: any): number => {
    if (transitTime === undefined || transitTime === null) return Infinity;
    const str = String(transitTime).trim();
    if (!str) return Infinity;
    const matches = str.match(/\d+/g);
    if (!matches || matches.length === 0) return Infinity;
    return parseFloat(matches[0]);
  };

  const getPriceValue = (totalPricing: any): number => {
    if (totalPricing === undefined || totalPricing === null) return Infinity;
    const val = typeof totalPricing === 'number' ? totalPricing : parseFloat(totalPricing);
    return isNaN(val) ? Infinity : val;
  };

  // Find minimum price
  let minPrice = Infinity;
  let cheapestIndex = -1;
  for (let i = 0; i < charges.length; i++) {
    const price = getPriceValue(charges[i].totalPricing);
    if (price < minPrice) {
      minPrice = price;
      cheapestIndex = i;
    }
  }

  // Find minimum transit time
  let minTransit = Infinity;
  for (let i = 0; i < charges.length; i++) {
    const transit = getTransitTimeValue(charges[i].transitTime);
    if (transit < minTransit) {
      minTransit = transit;
    }
  }

  const cheapestCSP = cheapestIndex !== -1 ? charges[cheapestIndex] : null;
  const fastestCSPs: CSPChargeType[] = [];
  const remainingCSPs: CSPChargeType[] = [];

  for (let i = 0; i < charges.length; i++) {
    const charge = charges[i];
    if (cheapestCSP && charge === cheapestCSP) {
      continue;
    }

    const transit = getTransitTimeValue(charge.transitTime);
    if (transit !== Infinity && transit === minTransit) {
      fastestCSPs.push(charge);
    } else {
      remainingCSPs.push(charge);
    }
  }

  const result: CSPChargeType[] = [];
  if (cheapestCSP) {
    result.push(cheapestCSP);
  }
  result.push(...fastestCSPs);
  result.push(...remainingCSPs);

  return result;
}

export default SingleCSP;
